import { Workbook } from "exceljs";
import dayjs from "dayjs";
import {
	EnrollmentSummaryConfigurableFilters,
	reportsService,
} from "../../services/reports.service";
import { addCoverSheet } from "./cover-sheet";
import { applyHeaderStyle, currencyFmt, filenameSlug } from "./workbook-styles";
import { BuiltWorkbook } from "./build-workbook";

export interface EnrollmentSummaryWorkbookFilters
	extends EnrollmentSummaryConfigurableFilters {
	programName?: string;
	currencyMask?: string;
	orgUnitFilter?: string;
	researcherFilter?: string;
}

const titleCase = (s: string): string =>
	s ? s.charAt(0) + s.slice(1).toLowerCase() : s;

const formatPeriod = (start: string | null, end: string | null): string => {
	const s = start ? dayjs(start).format("YYYY-MM-DD") : "—";
	const e = end ? dayjs(end).format("YYYY-MM-DD") : "active";
	return `${s} – ${e}`;
};

/**
 * SIMS-7588 — Build the Enrollment Summary (configurable) Excel export ENTIRELY
 * IN THE BROWSER, for the same reason as the Visit Detail export: the backend
 * `/reports/enrollment-summary-configurable/export` binary body arrived through
 * API Gateway + CloudFront as undecoded base64 and could not be opened in Excel.
 * We fetch the same JSON the PDF uses (fetchAllEnrollmentSummaryConfigurable)
 * and write the workbook with exceljs — no binary crosses API Gateway.
 *
 * Columns mirror the on-screen table / PDF: fixed enrollment fields, then the
 * program's dynamic result columns, then the incentive/balance columns.
 */
export const buildEnrollmentSummaryWorkbook = async (
	filters: EnrollmentSummaryWorkbookFilters,
): Promise<BuiltWorkbook> => {
	const { columns, rows } =
		await reportsService.fetchAllEnrollmentSummaryConfigurable({
			accountId: filters.accountId,
			programId: filters.programId,
			status: filters.status,
			orgUnitId: filters.orgUnitId,
			providerId: filters.providerId,
			filterSummary: filters.filterSummary,
		});

	const programName = filters.programName ?? "Program";
	const moneyFmt = currencyFmt(filters.currencyMask ?? "$");

	const workbook = new Workbook();
	workbook.creator = "SIMS Reporting";
	workbook.created = new Date();

	addCoverSheet(workbook, {
		programName,
		exportedAt: new Date(),
		orgUnitFilter: filters.orgUnitFilter,
		researcherFilter: filters.researcherFilter,
	});

	const sheet = workbook.addWorksheet("Enrollment Summary");

	// Dynamic result columns are keyed as result_<key> to avoid colliding with
	// the fixed column keys.
	const resultCols = columns.map((col) => ({
		header: col.label,
		key: `result_${col.key}`,
		width: 12,
	}));

	sheet.columns = [
		{ header: "CIN", key: "cin", width: 14 },
		{ header: "Name", key: "name", width: 24 },
		{ header: "Site", key: "site", width: 18 },
		{ header: "Provider", key: "provider", width: 20 },
		{ header: "NPI", key: "npi", width: 14 },
		{ header: "Enrollment Period", key: "period", width: 24 },
		{ header: "Status", key: "status", width: 14 },
		{ header: "Payer Plan Name", key: "payerPlanName", width: 18 },
		{ header: "Payer Plan #", key: "payerPlanNumber", width: 14 },
		...resultCols,
		{ header: "Incentive Earned", key: "incentiveEarned", width: 16 },
		{ header: "Incentive Received", key: "incentiveDisbursed", width: 18 },
		{ header: "Bank Balance", key: "bankBalance", width: 14 },
	];
	applyHeaderStyle(sheet.getRow(1));

	for (const r of rows) {
		const rowValues: Record<string, string | number> = {
			cin: r.cin ?? "",
			name: r.beneficiaryName ?? "",
			site: r.site ?? "",
			provider: r.provider ?? "",
			npi: r.npi ?? "",
			period: formatPeriod(r.startDate, r.endDate),
			status: titleCase(r.status),
			payerPlanName: r.payerPlanName ?? "",
			payerPlanNumber: r.payerPlanNumber ?? "",
			incentiveEarned: r.incentiveEarned ?? 0,
			incentiveDisbursed: r.incentiveDisbursed ?? 0,
			bankBalance: r.bankBalance ?? 0,
		};
		for (const col of columns) {
			rowValues[`result_${col.key}`] = r.resultCounts?.[col.key] ?? 0;
		}
		const row = sheet.addRow(rowValues);
		row.getCell("incentiveEarned").numFmt = moneyFmt;
		row.getCell("incentiveDisbursed").numFmt = moneyFmt;
		row.getCell("bankBalance").numFmt = moneyFmt;
	}

	const arrayBuffer = await workbook.xlsx.writeBuffer();
	const blob = new Blob([arrayBuffer], {
		type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	});
	const filename = `enrollment_summary_${filenameSlug(programName)}.xlsx`;
	return { blob, filename };
};
